import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["portalId", "appId", "appName", "oAuthScope", "oAuthScopes", "origin", "mcpParams", "customParams", "accountId", "oAuthorMigrationParams", "incremental", "mode", "modeSettings", "onComplete", "onCancel", "onAuthError", "config", "onClick"];
import { useState, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Button } from 'foundations-components/transitional/components/Button';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { Alert } from 'foundations-components/transitional/components/Alert';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/transitional/components/View';
import { UnifiedAccountModal as CoreUnifiedAccountModal } from '../account/UnifiedAccountModal';
import { useHeadlessOAuthLauncher, HeadlessOAuthWindowProvider } from '../shared/headless';
import { deleteAccount as deleteAccountApi, uninstallApp as uninstallAppApi } from '../account/api/UnifiedAccountApi';
import { InitWrapper } from './components/InitWrapper';
import { captureException, toError } from '../internal/ravenUtils';
import { markCleanupHandled } from '../shared/utils/cleanupHandledError';

/**
 * The props type for the internal, mode-discriminated `CoreUnifiedAccountModal`.
 * Named via `Parameters<>` (rather than importing its exported prop type
 * directly) to sidestep a name collision with this file's own, differently-shaped
 * `UnifiedAccountModalProps` (from `./types`).
 */
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Deletes the account created for a completion attempt that ended up
 * failing (either the completion hook rejected, or the consumer's onComplete
 * threw), honoring `shouldDeleteOnFailure`.
 * Reauth operates on a pre-existing account, so a failure here must not
 * delete it or trigger downstream data cleanup — `isReauth` exempts it.
 * Marks `error` so UnifiedAccountModal's own handleComplete — which sits one
 * layer up and awaits this one's rejection — doesn't clean up a second time.
 * Always rethrows `error` once cleanup (if any) is done.
 *
 * The delete runs before reporting so that a hypothetical captureException
 * failure never blocks the critical cleanup path.
 */
async function cleanupAccountAfterCompleteFailure(error, newAccountId, appId, shouldDeleteOnFailure, isReauth) {
  markCleanupHandled(error);
  const shouldCleanup = !isReauth && (shouldDeleteOnFailure ? shouldDeleteOnFailure(newAccountId) : true);
  if (shouldCleanup) {
    try {
      await deleteAccountApi(newAccountId);
    } catch (cleanupError) {
      captureException(new Error('Failed to clean up account after hook error', {
        cause: toError(cleanupError)
      }), {
        extra: {
          accountId: newAccountId,
          appId
        }
      });
    }
  }
  try {
    captureException(new Error('onComplete callback failed after account creation', {
      cause: toError(error)
    }), {
      extra: {
        accountId: newAccountId,
        appId
      }
    });
  } catch (_unused) {
    // Best-effort — never let reporting block the rethrow.
  }
  throw error;
}

/**
 * How `onAuthError` works:
 *
 *   trigger (Button onClick / useModal.openModal)
 *     │
 *     ▼
 *   UnifiedAccountModal
 *     ├─ mode="headless" ──► UnifiedHeadlessAuthView
 *     │    ├─ config fails to load ───────────► reportHeadlessAuthError(AuthenticateError)
 *     │    ├─ flow needs input (steps > 0) ──► falls back to UnifiedAuthView (modal)
 *     │    │                                     └─ onAuthError forwarded (see modal path)
 *     │    └─ pure OAuth ──► HeadlessAuthDriver
 *     │                         └─ machine FINISH_FAILURE ─► reportHeadlessAuthError(error)
 *     │
 *     └─ mode="modal" ──► UnifiedAuthView
 *          └─ machine FINISH_FAILURE ─► reportError action ─► onAuthError(error)
 *               (informative: modal still renders its own error UI)
 *
 *   error.type ∈ { AuthenticateError, AuthorizeError, DuplicateAccount,
 *                  OAuthWindowBlocked, OAuthWindowClosed, OAuthWindowTimeout,
 *                  UnknownOAuthProblem }
 *
 *   Note: OAuthWindow* errors are reported in headless mode only (via reportHeadlessAuthError).
 *   In modal mode, onAuthError fires for FINISH_FAILURE errors only (AuthenticateError,
 *   AuthorizeError, DuplicateAccount) — OAuthWindow* errors are handled by the modal UI.
 */
export function buildUnifiedAccount(options) {
  const {
    displayName,
    defaultConfig,
    defaultProps,
    defaultModeSettings,
    onInit,
    onConnectComplete,
    onReconnectComplete,
    onDeleteComplete,
    onUninstallComplete,
    onAuthError: defaultOnAuthError
  } = options;
  async function executeHandleComplete(newAccountId, finalAppId, finalPortalId, onCompleteFn, shouldDeleteOnFailure, isReconnect, isReauth, hookOverride) {
    const hook = hookOverride !== undefined ? hookOverride : isReconnect ? onReconnectComplete : onConnectComplete;
    if (hook && newAccountId !== undefined && finalAppId !== undefined && finalPortalId !== undefined) {
      try {
        await hook(newAccountId, {
          appId: finalAppId,
          portalId: finalPortalId
        });
        await onCompleteFn({
          accountId: newAccountId
        });
      } catch (error) {
        await cleanupAccountAfterCompleteFailure(error, newAccountId, finalAppId, shouldDeleteOnFailure, isReauth);
        throw error;
      }
    } else {
      try {
        await onCompleteFn({
          accountId: newAccountId
        });
      } catch (error) {
        if (newAccountId !== undefined) {
          await cleanupAccountAfterCompleteFailure(error, newAccountId, finalAppId, shouldDeleteOnFailure, isReauth);
        } else {
          markCleanupHandled(error);
        }
        throw error;
      }
    }
  }
  const UnifiedAccountModalInternal = ({
    inline = false,
    portalId: propPortalId,
    appId: propAppId,
    appName: propAppName,
    oAuthScope: propOAuthScope,
    oAuthScopes: propOAuthScopes,
    origin: propOrigin,
    mcpParams: propMcpParams,
    customParams: propCustomParams,
    accountId,
    oAuthorMigrationParams,
    incremental: propIncremental,
    onComplete,
    onCancel,
    config,
    mode: propMode,
    modeSettings: propModeSettings,
    onAuthError
  }) => {
    const modeSettings = propModeSettings !== null && propModeSettings !== void 0 ? propModeSettings : defaultModeSettings;
    const mode = propMode !== null && propMode !== void 0 ? propMode : defaultProps === null || defaultProps === void 0 ? void 0 : defaultProps.mode;
    const portalId = propPortalId !== null && propPortalId !== void 0 ? propPortalId : defaultProps === null || defaultProps === void 0 ? void 0 : defaultProps.portalId;
    const appId = propAppId !== null && propAppId !== void 0 ? propAppId : defaultProps === null || defaultProps === void 0 ? void 0 : defaultProps.appId;
    const incremental = propIncremental !== null && propIncremental !== void 0 ? propIncremental : defaultProps === null || defaultProps === void 0 ? void 0 : defaultProps.incremental;
    const origin = propOrigin !== null && propOrigin !== void 0 ? propOrigin : defaultProps === null || defaultProps === void 0 ? void 0 : defaultProps.origin;
    const mergedConfig = Object.assign({}, defaultConfig, config);
    const {
      shouldDeleteOnFailure,
      useIntroScreen,
      useSuccessScreen,
      disableOnComplete
    } = mergedConfig;
    const effectiveOnAuthError = onAuthError && defaultOnAuthError ? error => {
      try {
        onAuthError(error);
      } finally {
        defaultOnAuthError(error);
      }
    } : onAuthError !== null && onAuthError !== void 0 ? onAuthError : defaultOnAuthError;
    const isReconnect = !!oAuthorMigrationParams || accountId !== undefined;
    // Migration reconnect still creates a brand-new account under the hood
    // (see createFinishMigrationReconnectHandler), so only a plain reauth of
    // a pre-existing accountId should be exempt from rollback deletion.
    const isReauth = !oAuthorMigrationParams && accountId !== undefined;
    const completeHookOverride = isReconnect ? mergedConfig.onReconnectComplete : mergedConfig.onConnectComplete;
    const handleComplete = useCallback((newAccountId, resolvedAppId, resolvedPortalId) => executeHandleComplete(newAccountId, resolvedAppId !== null && resolvedAppId !== void 0 ? resolvedAppId : appId, resolvedPortalId !== null && resolvedPortalId !== void 0 ? resolvedPortalId : portalId, onComplete, shouldDeleteOnFailure, isReconnect, isReauth, completeHookOverride), [onComplete, shouldDeleteOnFailure, appId, portalId, isReconnect, isReauth, completeHookOverride]);

    // An account the user already has skips the connect hook (it was bound
    // when first connected) and is never rolled back if onComplete fails.
    const handleSelectExistingAccount = useCallback(existingAccountId => onComplete({
      accountId: existingAccountId
    }), [onComplete]);
    if (portalId === undefined || appId === undefined) {
      return /*#__PURE__*/_jsx(Alert, {
        type: "danger",
        titleText: "Configuration Error",
        children: /*#__PURE__*/_jsx(View, {
          paddingBlock: "200",
          paddingInline: "200",
          children: /*#__PURE__*/_jsx(Text, {
            children: "portalId and appId are required"
          })
        })
      });
    }
    const renderModal = resolvedProps => {
      var _ref, _ref2, _ref3, _ref4, _ref5, _ref6, _ref7, _ref8;
      const appName = (_ref = (_ref2 = propAppName !== null && propAppName !== void 0 ? propAppName : resolvedProps.appName) !== null && _ref2 !== void 0 ? _ref2 : defaultProps === null || defaultProps === void 0 ? void 0 : defaultProps.appName) !== null && _ref !== void 0 ? _ref : undefined;
      const oAuthScopes = (_ref3 = (_ref4 = (_ref5 = (_ref6 = propOAuthScopes !== null && propOAuthScopes !== void 0 ? propOAuthScopes : propOAuthScope ? propOAuthScope.split(' ').filter(Boolean) : undefined) !== null && _ref6 !== void 0 ? _ref6 : resolvedProps.oAuthScopes) !== null && _ref5 !== void 0 ? _ref5 : resolvedProps.oAuthScope ? resolvedProps.oAuthScope.split(' ').filter(Boolean) : undefined) !== null && _ref4 !== void 0 ? _ref4 : defaultProps === null || defaultProps === void 0 ? void 0 : defaultProps.oAuthScopes) !== null && _ref3 !== void 0 ? _ref3 : defaultProps !== null && defaultProps !== void 0 && defaultProps.oAuthScope ? defaultProps.oAuthScope.split(' ').filter(Boolean) : undefined;
      const mcpParams = (_ref7 = propMcpParams !== null && propMcpParams !== void 0 ? propMcpParams : resolvedProps.mcpParams) !== null && _ref7 !== void 0 ? _ref7 : defaultProps === null || defaultProps === void 0 ? void 0 : defaultProps.mcpParams;
      const customParams = (_ref8 = propCustomParams !== null && propCustomParams !== void 0 ? propCustomParams : resolvedProps.customParams) !== null && _ref8 !== void 0 ? _ref8 : defaultProps === null || defaultProps === void 0 ? void 0 : defaultProps.customParams;

      // `mode`/`modeSettings` are resolved independently above (the latter
      // never falls back to `defaultProps`, which has no per-mode settings
      // of its own), so their correlation can't be proven statically here —
      // asserted once, as a whole, rather than piecemeal per field.
      const coreModalProps = {
        inline,
        portalId,
        appId,
        appName,
        oAuthScopes,
        origin,
        mcpParams,
        customParams,
        accountId,
        oAuthorMigrationParams,
        incremental,
        mode,
        modeSettings,
        onComplete: newAccountId => handleComplete(newAccountId, appId, portalId),
        onSelectExistingAccount: handleSelectExistingAccount,
        onCancel,
        onAuthError: effectiveOnAuthError,
        config: {
          shouldDeleteOnFailure,
          useIntroScreen,
          useSuccessScreen,
          disableOnComplete
        }
      };
      return /*#__PURE__*/_jsx(CoreUnifiedAccountModal, Object.assign({}, coreModalProps));
    };
    if (onInit) {
      return /*#__PURE__*/_jsx(InitWrapper, {
        appId: appId,
        portalId: portalId,
        onInit: onInit,
        children: renderModal
      });
    }
    return renderModal({});
  };
  UnifiedAccountModalInternal.displayName = `${displayName}Modal`;
  function UnifiedAccountButtonInternalFn(_ref9) {
    var _ref10, _ref11;
    let {
        portalId: propPortalId,
        appId: propAppId,
        appName: propAppName,
        oAuthScope: propOAuthScope,
        oAuthScopes: propOAuthScopes,
        origin: propOrigin,
        mcpParams: propMcpParams,
        customParams: propCustomParams,
        accountId,
        oAuthorMigrationParams,
        incremental: propIncremental,
        mode: propMode,
        modeSettings: propModeSettings,
        onComplete,
        onCancel = () => {},
        onAuthError,
        config,
        onClick
      } = _ref9,
      buttonProps = _objectWithoutPropertiesLoose(_ref9, _excluded);
    const [showAccountModal, setShowAccountModal] = useState(false);
    const modeSettings = propModeSettings !== null && propModeSettings !== void 0 ? propModeSettings : defaultModeSettings;
    const mergedConfig = Object.assign({}, defaultConfig, config);
    const mode = propMode !== null && propMode !== void 0 ? propMode : defaultProps === null || defaultProps === void 0 ? void 0 : defaultProps.mode;
    const portalId = propPortalId !== null && propPortalId !== void 0 ? propPortalId : defaultProps === null || defaultProps === void 0 ? void 0 : defaultProps.portalId;
    const appId = propAppId !== null && propAppId !== void 0 ? propAppId : defaultProps === null || defaultProps === void 0 ? void 0 : defaultProps.appId;
    const appName = propAppName !== null && propAppName !== void 0 ? propAppName : defaultProps === null || defaultProps === void 0 ? void 0 : defaultProps.appName;
    const oAuthScopes = (_ref10 = (_ref11 = propOAuthScopes !== null && propOAuthScopes !== void 0 ? propOAuthScopes : propOAuthScope ? propOAuthScope.split(' ').filter(Boolean) : undefined) !== null && _ref11 !== void 0 ? _ref11 : defaultProps === null || defaultProps === void 0 ? void 0 : defaultProps.oAuthScopes) !== null && _ref10 !== void 0 ? _ref10 : defaultProps !== null && defaultProps !== void 0 && defaultProps.oAuthScope ? defaultProps.oAuthScope.split(' ').filter(Boolean) : undefined;
    const mcpParams = propMcpParams !== null && propMcpParams !== void 0 ? propMcpParams : defaultProps === null || defaultProps === void 0 ? void 0 : defaultProps.mcpParams;
    const incremental = propIncremental !== null && propIncremental !== void 0 ? propIncremental : defaultProps === null || defaultProps === void 0 ? void 0 : defaultProps.incremental;
    const customParams = propCustomParams !== null && propCustomParams !== void 0 ? propCustomParams : defaultProps === null || defaultProps === void 0 ? void 0 : defaultProps.customParams;
    const origin = propOrigin !== null && propOrigin !== void 0 ? propOrigin : defaultProps === null || defaultProps === void 0 ? void 0 : defaultProps.origin;
    const {
      openOAuthPopup,
      oAuthWindow,
      flowKey
    } = useHeadlessOAuthLauncher(mode, appName);
    const closeModal = () => setShowAccountModal(false);
    const handleButtonClick = event => {
      openOAuthPopup();
      setShowAccountModal(true);
      onClick === null || onClick === void 0 || onClick(event);
    };

    // See the `coreModalProps` comment above: `mode`/`modeSettings`
    // correlation can't be proven statically once `mode` has been merged
    // with `defaultProps.mode`, so it's asserted once here.
    const modalProps = {
      key: flowKey,
      portalId,
      appId,
      appName,
      oAuthScopes,
      origin,
      mcpParams,
      customParams,
      accountId,
      oAuthorMigrationParams,
      incremental,
      mode,
      modeSettings,
      onAuthError,
      config: mergedConfig,
      onComplete: response => {
        // The account is already fully created and confirmed good by this
        // point, so there's nothing to clean up if this (consumer-supplied,
        // possibly async) notification callback rejects — intentionally not
        // awaited.
        void onComplete(response);
        if (!mergedConfig.useSuccessScreen) {
          closeModal();
        }
      },
      onCancel: () => {
        closeModal();
        onCancel();
      }
    };
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(Button, Object.assign({}, buttonProps, {
        onClick: handleButtonClick
      })), showAccountModal && /*#__PURE__*/_jsx(HeadlessOAuthWindowProvider, {
        oAuthWindow: oAuthWindow,
        children: /*#__PURE__*/_jsx(UnifiedAccountModalInternal, Object.assign({}, modalProps))
      })]
    });
  }
  UnifiedAccountButtonInternalFn.displayName = `${displayName}Button`;
  const UnifiedAccountButtonInternal = UnifiedAccountButtonInternalFn;
  function useUnifiedAccountModal(modalOptions, callbacks) {
    var _ref12, _ref13, _ref14;
    const {
      portalId: propPortalId,
      appId: propAppId,
      appName: propAppName,
      oAuthScope: propOAuthScope,
      oAuthScopes: propOAuthScopes,
      origin: propOrigin,
      mcpParams: propMcpParams,
      customParams: propCustomParams,
      accountId,
      oAuthorMigrationParams,
      incremental: propIncremental,
      mode: propMode,
      modeSettings: propModeSettings,
      onAuthError,
      config,
      usePortal = true
    } = modalOptions;
    const modeSettings = propModeSettings !== null && propModeSettings !== void 0 ? propModeSettings : defaultModeSettings;
    const mode = propMode !== null && propMode !== void 0 ? propMode : defaultProps === null || defaultProps === void 0 ? void 0 : defaultProps.mode;
    const portalId = propPortalId !== null && propPortalId !== void 0 ? propPortalId : defaultProps === null || defaultProps === void 0 ? void 0 : defaultProps.portalId;
    const appId = propAppId !== null && propAppId !== void 0 ? propAppId : defaultProps === null || defaultProps === void 0 ? void 0 : defaultProps.appId;
    const appName = (_ref12 = propAppName !== null && propAppName !== void 0 ? propAppName : defaultProps === null || defaultProps === void 0 ? void 0 : defaultProps.appName) !== null && _ref12 !== void 0 ? _ref12 : undefined;
    const oAuthScopes = (_ref13 = (_ref14 = propOAuthScopes !== null && propOAuthScopes !== void 0 ? propOAuthScopes : propOAuthScope ? propOAuthScope.split(' ').filter(Boolean) : undefined) !== null && _ref14 !== void 0 ? _ref14 : defaultProps === null || defaultProps === void 0 ? void 0 : defaultProps.oAuthScopes) !== null && _ref13 !== void 0 ? _ref13 : defaultProps !== null && defaultProps !== void 0 && defaultProps.oAuthScope ? defaultProps.oAuthScope.split(' ').filter(Boolean) : undefined;
    const mcpParams = propMcpParams !== null && propMcpParams !== void 0 ? propMcpParams : defaultProps === null || defaultProps === void 0 ? void 0 : defaultProps.mcpParams;
    const incremental = propIncremental !== null && propIncremental !== void 0 ? propIncremental : defaultProps === null || defaultProps === void 0 ? void 0 : defaultProps.incremental;
    const customParams = propCustomParams !== null && propCustomParams !== void 0 ? propCustomParams : defaultProps === null || defaultProps === void 0 ? void 0 : defaultProps.customParams;
    const origin = propOrigin !== null && propOrigin !== void 0 ? propOrigin : defaultProps === null || defaultProps === void 0 ? void 0 : defaultProps.origin;
    const {
      onComplete,
      onCancel
    } = callbacks;
    const mergedConfig = Object.assign({}, defaultConfig, config);
    const {
      shouldDeleteOnFailure,
      useIntroScreen,
      useSuccessScreen,
      disableOnComplete
    } = mergedConfig;
    const effectiveOnAuthError = onAuthError && defaultOnAuthError ? error => {
      try {
        onAuthError(error);
      } finally {
        defaultOnAuthError(error);
      }
    } : onAuthError !== null && onAuthError !== void 0 ? onAuthError : defaultOnAuthError;
    const isReconnect = !!oAuthorMigrationParams || accountId !== undefined;
    // Migration reconnect still creates a brand-new account under the hood
    // (see createFinishMigrationReconnectHandler), so only a plain reauth of
    // a pre-existing accountId should be exempt from rollback deletion.
    const isReauth = !oAuthorMigrationParams && accountId !== undefined;
    const completeHookOverride = isReconnect ? mergedConfig.onReconnectComplete : mergedConfig.onConnectComplete;
    const [isOpen, setIsOpen] = useState(false);
    const [initState, setInitState] = useState(onInit ? {
      status: 'idle'
    } : {
      status: 'ready',
      resolvedProps: {}
    });
    const portalContainerRef = useRef(null);
    const {
      openOAuthPopup,
      oAuthWindow,
      flowKey
    } = useHeadlessOAuthLauncher(mode, appName);
    useEffect(() => {
      if (!usePortal) return;
      const container = document.createElement('div');
      container.setAttribute('data-unified-account-modal-portal', 'true');
      document.body.appendChild(container);
      portalContainerRef.current = container;
      return () => {
        if (portalContainerRef.current && document.body.contains(portalContainerRef.current)) {
          document.body.removeChild(portalContainerRef.current);
          portalContainerRef.current = null;
        }
      };
    }, [usePortal]);
    useEffect(() => {
      if (!onInit || !isOpen || initState.status !== 'idle') return;
      if (appId === undefined || portalId === undefined) return;
      let isMounted = true;
      const runInit = async () => {
        try {
          setInitState({
            status: 'loading'
          });
          const resolvedProps = await onInit({
            appId,
            portalId
          });
          if (isMounted) {
            setInitState({
              status: 'ready',
              resolvedProps
            });
          }
        } catch (err) {
          if (isMounted) {
            setInitState({
              status: 'error',
              message: err instanceof Error ? err.message : 'Failed to initialize component'
            });
          }
        }
      };
      void runInit();
      return () => {
        isMounted = false;
      };
    }, [isOpen, appId, portalId, initState.status]);
    const openModal = useCallback(() => {
      openOAuthPopup();
      if (onInit) {
        setInitState({
          status: 'idle'
        });
      }
      setIsOpen(true);
    }, [openOAuthPopup]);
    const closeModal = useCallback(() => setIsOpen(false), []);
    const handleComplete = useCallback(async newAccountId => {
      let succeeded = false;
      try {
        await executeHandleComplete(newAccountId, appId, portalId, onComplete, shouldDeleteOnFailure, isReconnect, isReauth, completeHookOverride);
        succeeded = true;
      } finally {
        if (succeeded && !useSuccessScreen) {
          closeModal();
        }
      }
    }, [onComplete, shouldDeleteOnFailure, useSuccessScreen, closeModal, appId, portalId, isReconnect, isReauth, completeHookOverride]);

    // See `handleSelectExistingAccount` in `UnifiedAccountModalInternal`.
    // Closes like handleComplete: a success screen, if any, renders instead.
    const handleSelectExistingAccount = useCallback(async existingAccountId => {
      await onComplete({
        accountId: existingAccountId
      });
      if (!useSuccessScreen) {
        closeModal();
      }
    }, [onComplete, useSuccessScreen, closeModal]);
    const handleCancel = useCallback(() => {
      closeModal();
      onCancel === null || onCancel === void 0 || onCancel();
    }, [closeModal, onCancel]);
    const renderModalContent = () => {
      if (appId === undefined || portalId === undefined) {
        return /*#__PURE__*/_jsx(Alert, {
          type: "danger",
          titleText: "Configuration Error",
          children: /*#__PURE__*/_jsx(View, {
            paddingBlock: "200",
            paddingInline: "200",
            children: /*#__PURE__*/_jsx(Text, {
              children: "portalId and appId are required"
            })
          })
        });
      }
      if (initState.status === 'loading') {
        return /*#__PURE__*/_jsxs(View, {
          textAlign: "center",
          paddingBlock: "300",
          children: [/*#__PURE__*/_jsx(LoadingSpinner, {}), /*#__PURE__*/_jsx(View, {
            marginBlockStart: "200",
            children: /*#__PURE__*/_jsx(Text, {
              children: "Loading..."
            })
          })]
        });
      }
      if (initState.status === 'error') {
        return /*#__PURE__*/_jsx(Alert, {
          type: "danger",
          titleText: "Initialization Error",
          children: /*#__PURE__*/_jsx(View, {
            paddingBlock: "200",
            paddingInline: "200",
            children: /*#__PURE__*/_jsx(Text, {
              children: initState.message
            })
          })
        });
      }
      if (initState.status === 'ready') {
        var _ref15, _ref16;
        const resolvedAppName = (_ref15 = appName !== null && appName !== void 0 ? appName : initState.resolvedProps.appName) !== null && _ref15 !== void 0 ? _ref15 : undefined;
        const resolvedOAuthScopes = (_ref16 = oAuthScopes !== null && oAuthScopes !== void 0 ? oAuthScopes : initState.resolvedProps.oAuthScopes) !== null && _ref16 !== void 0 ? _ref16 : initState.resolvedProps.oAuthScope ? initState.resolvedProps.oAuthScope.split(' ').filter(Boolean) : undefined;
        const resolvedMcpParams = mcpParams !== null && mcpParams !== void 0 ? mcpParams : initState.resolvedProps.mcpParams;
        const resolvedCustomParams = customParams !== null && customParams !== void 0 ? customParams : initState.resolvedProps.customParams;

        // See the `coreModalProps` comment in `UnifiedAccountModalInternal`
        // above: `mode`/`modeSettings` correlation can't be proven
        // statically once `mode` has been merged with `defaultProps.mode`.
        const coreModalProps = {
          key: flowKey,
          portalId,
          appId,
          appName: resolvedAppName,
          oAuthScopes: resolvedOAuthScopes,
          origin,
          mcpParams: resolvedMcpParams,
          customParams: resolvedCustomParams,
          accountId,
          oAuthorMigrationParams,
          incremental,
          mode,
          modeSettings,
          onComplete: handleComplete,
          onSelectExistingAccount: handleSelectExistingAccount,
          onCancel: handleCancel,
          onAuthError: effectiveOnAuthError,
          config: {
            shouldDeleteOnFailure,
            useIntroScreen,
            useSuccessScreen,
            disableOnComplete
          }
        };
        return /*#__PURE__*/_jsx(CoreUnifiedAccountModal, Object.assign({}, coreModalProps));
      }
      return null;
    };
    const modalElement = isOpen ? /*#__PURE__*/_jsx(HeadlessOAuthWindowProvider, {
      oAuthWindow: oAuthWindow,
      children: renderModalContent()
    }) : null;
    const Modal = usePortal && isOpen && portalContainerRef.current ? /*#__PURE__*/createPortal(modalElement, portalContainerRef.current) : modalElement;
    return {
      openModal,
      closeModal,
      isOpen,
      Modal
    };
  }
  async function wrappedDeleteAccount(accountId) {
    const result = await deleteAccountApi(accountId);
    if (onDeleteComplete) {
      try {
        await onDeleteComplete(accountId);
      } catch (error) {
        // Swallowed on purpose: the account is already deleted, so the caller
        // should still see success. Report it so the failure is not invisible.
        captureException(new Error('onDeleteComplete hook failed', {
          cause: toError(error)
        }), {
          extra: {
            accountId
          }
        });
      }
    }
    return result;
  }
  async function wrappedUninstallApp(appId) {
    const result = await uninstallAppApi(appId);
    if (onUninstallComplete) {
      try {
        await onUninstallComplete(appId);
      } catch (error) {
        // Swallowed on purpose: the app is already uninstalled, so the caller
        // should still see success. Report it so the failure is not invisible.
        captureException(new Error('onUninstallComplete hook failed', {
          cause: toError(error)
        }), {
          extra: {
            appId
          }
        });
      }
    }
    return result;
  }
  return {
    Button: UnifiedAccountButtonInternal,
    Modal: UnifiedAccountModalInternal,
    useModal: useUnifiedAccountModal,
    deleteAccount: wrappedDeleteAccount,
    uninstallApp: wrappedUninstallApp
  };
}